import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { ThemeProvider } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode, useCallback, useEffect, useMemo, useState } from 'react';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router-dom';

import { redirectToErrorPage } from 'api/axios/responses/interceptors/AuthErrorResponseInterceptor';
import i18nInstance from 'i18n/config';
import 'library/MuiProLicense';
import { PCTheme } from 'library/utils/themes/PCTheme';
import {
	AuthProviderStorage,
	AuthenticationProvider,
	initializeAuthProviderStorage,
} from 'main/auth/provider';
import { reactPlugin } from 'main/azureInsights/config';
import { getRouter } from 'main/routes/RoutesAggregator';
import { errors_AuthenticationUrl } from 'main/routes/urls/RouteUrls';
import { store } from 'store/configureStore';

// Create a client
const queryClient = new QueryClient();
const router = getRouter();

// Handles dynamic imports load failure, by refreshing the webpage. Will occur during deployment of the new version
globalThis.addEventListener('vite:preloadError', () => {
	globalThis.location.reload();
});

const handleError = (
	message?: string,
	redirectPage = errors_AuthenticationUrl
) => {
	if (message) {
		console.error(message);
	}
	redirectToErrorPage(redirectPage);
};

const App = () => {
	const [authProvider, setAuthProvider] = useState<
		AuthenticationProvider | undefined
	>(undefined);

	useEffect(() => {
		initializeAuthProviderStorage().then(setAuthProvider);
	}, []);

	const onAuthenticated = useCallback((isAuth: boolean, message?: string) => {
		if (!isAuth) {
			handleError(message);
		}
	}, []);

	const AuthProvider = useMemo(() => {
		if (authProvider == null) {
			return AuthProviderStorage.getDefaultProviderComponent();
		}

		authProvider.onAuthenticated(onAuthenticated);
		return authProvider.getComponent();
	}, [authProvider, onAuthenticated]);

	return (
		<I18nextProvider i18n={i18nInstance}>
			<StrictMode>
				<Provider store={store}>
					<QueryClientProvider client={queryClient}>
						<AppInsightsContext value={reactPlugin}>
							<ThemeProvider theme={PCTheme}>
								<AuthProvider>
									<RouterProvider router={router} />
								</AuthProvider>
							</ThemeProvider>
						</AppInsightsContext>
					</QueryClientProvider>
				</Provider>
			</StrictMode>
		</I18nextProvider>
	);
};

export default App;
